/**
 * Anzahlungsbeleg und Restzahlungs-/Gesamtzahlungsbeleg (Spec 2026-10-31-anzahlungsbeleg-design.md,
 * Asana 1220057765558651) - Neucode ohne Vorbild in `index.html`, deshalb kein Portierungsmodul (nicht in
 * `MODULE` von `scripts/gen-herkunft.ts`) und ohne Herkunftsmarken. Gegenstueck zu
 * `backend/src/domaene/ratenposition.ts`: dieselbe Regel, dieselbe Ordnung.
 *
 * Welcher Zahlungsbeleg zu einem Ist-Beleg gehoert, entscheidet die POSITION seiner Rate im Zahlungsplan der
 * Bestellung - nie der Typ des Dokuments und nie die Bezeichnung (`label`) der Rate. Die Position ist der
 * 1-basierte Rang nach `(sort_index, id)`; der Server liefert `o.payments` in genau dieser Ordnung (`GET
 * /api/orders` und die Antwort von `PUT .../payment-terms`), die Position einer Rate ist hier also ihr Index + 1.
 */
import type { IstZahlung, Order, OrderPayment } from "./types.ts";

export type Zahlungsbelegtyp = "anzahlungsbeleg" | "restzahlungsbeleg";

/**
 * Die ehemaligen Dokumenttypen des Zahlungsbelegs (vor der Aufteilung in Anzahlungs- und Restzahlungs-/Gesamtzahlungsbeleg) mit
 * ihrer damaligen Beschriftung - wortgleich mit `EHEMALIGE_DOKUMENTTYPEN` in `backend/src/documents/docTypes.ts`. Es sind KEINE
 * gueltigen Typen mehr, sie stehen aber in der Historie weiter (Entfernungsprotokoll `document_link_removals`, Ueberschreibungs-
 * Log der Bestellung). Dort heissen sie so, nie mit dem rohen Schluessel.
 */
export const EHEMALIGE_DOKUMENTTYPEN: Readonly<Record<string, string>> = Object.freeze({
  zahlungsbeleg: "Zahlungsbeleg",
  zahlungsbeleg_anzahlung: "Zahlungsbeleg Anzahlung",
  zahlungsbeleg_restzahlung: "Zahlungsbeleg Restzahlung",
});

/**
 * Die Beschriftung eines ehemaligen Dokumenttyps, sonst `undefined` (der Aufrufer faellt dann auf den rohen Schluessel zurueck).
 * `Object.hasOwn` statt Objektzugriff, damit `constructor` & Co. nicht als Beschriftung durchgehen.
 */
export function ehemaligerDokumenttypLabel(schluessel: unknown): string | undefined {
  return typeof schluessel === "string" && Object.hasOwn(EHEMALIGE_DOKUMENTTYPEN, schluessel)
    ? EHEMALIGE_DOKUMENTTYPEN[schluessel]
    : undefined;
}

/** Die beiden Dokumenttypen, die ein Beleg an einer Ist-Zahlung erfuellen kann (Gegenstueck zu `ZAHLUNGSBELEG_TYPEN` im Backend). */
export const ZAHLUNGSBELEG_TYPEN: ReadonlySet<string> = new Set<Zahlungsbelegtyp>([
  "anzahlungsbeleg",
  "restzahlungsbeleg",
]);

/**
 * Der Belegtyp einer Rate (`position` 1-basiert): die letzte oder einzige Position ist der Restzahlungs-/Gesamt-
 * zahlungsbeleg, jede andere ein Anzahlungsbeleg (auch eine Zwischenzahlung - sie hat keinen eigenen Typ). Ohne
 * Zahlungsplan (`anzahl` 0) verhaelt sich die Bestellung wie eine Gesamtzahlung. Dieselbe Regel wie
 * `belegtypFuerRate` im Backend.
 */
export function belegtypFuerRate(position: number, anzahl: number): Zahlungsbelegtyp {
  return anzahl <= 1 || position >= anzahl ? "restzahlungsbeleg" : "anzahlungsbeleg";
}

function ratenVon(o: Order): OrderPayment[] {
  return Array.isArray(o.payments) ? (o.payments as OrderPayment[]) : [];
}

/**
 * Ob der Anzahlungsbeleg Pflicht ist: mehr als eine Rate und das Zahlungsziel nie gewechselt (Damian, 01.11.2026;
 * Merker `zahlungszielGewechseltAm`, `null` und ein fehlender Schluessel heissen beide "nie gewechselt"). Die
 * einzige Stelle der Pflichtfrage im Frontend; Gegenstueck zu `anzahlungsbelegPflicht` im Backend.
 */
export function anzahlungsbelegPflicht(o: Order): boolean {
  return ratenVon(o).length > 1 && !o.zahlungszielGewechseltAm;
}

/**
 * Die Belege, die den Typ `typ` an einer Ist-Zahlung erfuellen: alle Dokumente an den Ist-Zahlungen (`actual.documents`)
 * der Raten, an deren Position `belegtypFuerRate` `typ` ergibt, in Planreihenfolge. Belege an einer Ist-Zahlung stehen
 * nicht in `o.pdfs`; die Drawer-Zeile zeigt sie deshalb gesondert unter dem Typ ("an der Zahlung").
 */
export function istBelegeFuer(o: Order, typ: Zahlungsbelegtyp): IstZahlung["documents"] {
  const raten = ratenVon(o);
  return raten.flatMap((rate, index) =>
    belegtypFuerRate(index + 1, raten.length) === typ ? (rate.actual?.documents ?? []) : [],
  );
}

/** Ob an einer Rate ein Ist-Beleg haengt, der den Typ `typ` erfuellt (`istBelegeFuer` ist nicht leer). */
export function istBelegFuer(o: Order, typ: Zahlungsbelegtyp): boolean {
  return istBelegeFuer(o, typ).length > 0;
}
